import { FormLabel } from '@material-ui/core';
import { forwardRef, ForwardRefRenderFunction, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { AutocompleteRef } from '@shared/components/autocomplete';
import { CompanyLookupCard } from '@shared/components/lookups/company-lookup';
import { MixedCompany } from '@shared/models/company/company';

import { styles } from './CompanySingleLookup.styles';
import { CompanyLookup, CompanyLookupProps } from '../CompanyLookup';

export interface CompanySingleLookupProps extends Omit<CompanyLookupProps, 'classes'>, WithStyles<typeof styles> {
  showRole?: boolean;
  value?: MixedCompany | null;
  onRemove?: () => void;
}

const CompanySingleLookupComponent: ForwardRefRenderFunction<AutocompleteRef, CompanySingleLookupProps> = (
  { classes, showRole, value, onRemove, ...lookupProps },
  ref
) => {
  const lookupRef = useRef<AutocompleteRef>(null);
  useImperativeHandle(ref, () => lookupRef.current as AutocompleteRef);

  const [localAutoFocus, setLocalAutoFocus] = useState<boolean>(false);

  // Reset the state once we select something
  useEffect(() => {
    if (value) {
      setLocalAutoFocus(false);
    }
  }, [value]);

  return (
    <div className={classes.root}>
      {!!value && (
        <>
          {lookupProps.label && (
            <FormLabel
              classes={{ root: classes.labelRoot, error: classes.labelError }}
              component="legend"
              error={lookupProps.error}
            >
              {lookupProps.label}
            </FormLabel>
          )}
          <CompanyLookupCard
            inputSize={lookupProps.inputProps?.size}
            item={value}
            showRole={showRole}
            onRemove={
              onRemove && !lookupProps.disabled
                ? () => {
                    if (onRemove) onRemove();

                    setLocalAutoFocus(true);
                  }
                : undefined
            }
          />
        </>
      )}
      {!value && (
        <CompanyLookup
          {...lookupProps}
          lookupClasses={{ ...lookupProps.lookupClasses, labelRoot: classes.labelRoot, labelError: classes.labelError }}
          inputProps={{ ...lookupProps.inputProps, autoFocus: localAutoFocus || lookupProps.inputProps?.autoFocus }}
          ref={lookupRef}
        />
      )}
    </div>
  );
};

export const CompanySingleLookup = withStyles(styles)(forwardRef(CompanySingleLookupComponent));
